<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { Copy } from "lucide-vue-next";
import { copyText } from "@/composables/useClipboard";

const props = defineProps<{
  lines: string[];
  /** Optional URL to a full raw log; "Open raw" link. */
  rawUrl?: string;
  /** Optional download href; "Download" link in v menu. */
  downloadUrl?: string;
}>();

const paused = ref(false);
const pausedNewLines = ref(0);
const scroller = ref<HTMLElement | null>(null);
const menuOpen = ref(false);

// Hard cap to keep DOM responsive. Spec says virtualise above 2k visible;
// for the MVP we slice to 2k and link to the raw log for the full tail.
const VISIBLE_CAP = 2000;
const visible = computed(() => {
  if (props.lines.length <= VISIBLE_CAP) return props.lines;
  return props.lines.slice(-VISIBLE_CAP);
});

watch(
  () => props.lines.length,
  (newLen, oldLen) => {
    if (paused.value) {
      pausedNewLines.value += newLen - oldLen;
      return;
    }
    void nextTick(() => {
      if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
    });
  },
);

function onScroll() {
  const el = scroller.value;
  if (!el) return;
  // If the user scrolls up, pause auto-scroll until they hit the bottom again.
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
  if (!atBottom && !paused.value) {
    paused.value = true;
    pausedNewLines.value = 0;
  } else if (atBottom && paused.value) {
    paused.value = false;
    pausedNewLines.value = 0;
  }
}

function togglePause() {
  paused.value = !paused.value;
  if (!paused.value) {
    pausedNewLines.value = 0;
    void nextTick(() => {
      if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
    });
  }
}

async function copyAll() {
  try {
    await copyText(visible.value.join("\n"));
  } catch {
    // No-op; clipboard can fail in insecure contexts.
  }
}

function onMouseEnter() {
  // Pause-on-hover; spec §3.4.
  paused.value = true;
}
function onMouseLeave() {
  // Only unpause if user wasn't scrolled up.
  const el = scroller.value;
  if (!el) return;
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
  if (atBottom) {
    paused.value = false;
    pausedNewLines.value = 0;
  }
}
</script>

<template>
  <div class="flex flex-col">
    <div class="mb-2 flex items-center justify-between">
      <span class="text-2xs text-fg-muted">
        <template v-if="paused">paused — {{ pausedNewLines }} new line<span v-if="pausedNewLines !== 1">s</span></template>
        <template v-else>live</template>
      </span>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="rounded border border-border px-2 py-1 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
          @click="togglePause"
        >
          {{ paused ? "resume" : "pause" }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
          @click="copyAll"
        >
          <Copy :size="12" /> copy
        </button>
        <div class="relative">
          <button
            type="button"
            class="rounded border border-border px-2 py-1 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
            @click="menuOpen = !menuOpen"
            aria-haspopup="menu"
            :aria-expanded="menuOpen"
          >v</button>
          <div
            v-if="menuOpen"
            class="absolute right-0 z-10 mt-1 w-44 rounded border border-border bg-bg p-1 text-2xs shadow dark:border-border-dark dark:bg-surface-dark"
            role="menu"
          >
            <a
              v-if="downloadUrl"
              :href="downloadUrl"
              class="block rounded px-2 py-1 hover:bg-surface dark:hover:bg-bg-dark"
              @click="menuOpen = false"
              download
            >Download full log</a>
            <a
              v-if="rawUrl"
              :href="rawUrl"
              target="_blank"
              rel="noreferrer noopener"
              class="block rounded px-2 py-1 hover:bg-surface dark:hover:bg-bg-dark"
              @click="menuOpen = false"
            >Open raw</a>
            <span v-if="!rawUrl && !downloadUrl" class="block px-2 py-1 text-fg-muted">No raw log yet.</span>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="scroller"
      class="h-72 min-h-[14rem] overflow-auto rounded border border-border bg-surface p-2 dark:border-border-dark dark:bg-surface-dark"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-atomic="false"
      @scroll="onScroll"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <pre class="mono whitespace-pre-wrap break-words text-[12px] leading-snug text-fg dark:text-fg-dark">{{ visible.join("\n") }}</pre>
    </div>
  </div>
</template>
